/**
 * One message in a correspondence thread (#1069).
 *
 * Extracted from `CorrespondencePanel.tsx`, which had reached 843 lines with the
 * message render inlined in `ThreadView` (the file has in-file helpers, but none
 * of them were extracted to their own module). Four separate asks from the
 * 2026-08-14 product review all land in this one row, and virtualization (an
 * unmet NFR) is blocked on the same split — so the extraction came first and the
 * presentation changes were made inside the small component afterwards.
 *
 * The thread should read like EMAIL, not like a forum:
 *
 *  - The viewer's own messages sit on the right, everyone else's on the left.
 *    "It's me and not me" is the ONLY axis. Not per-author colours — with more
 *    than two participants everyone else collapses onto the far side anyway —
 *    and explicitly NOT internal-vs-external, which is the space's job.
 *  - Every message gets a bordered container; the author and timestamp sit
 *    OUTSIDE it, so the border encloses the message and nothing else.
 *    (Story 5.5: the fill and the corner radii now follow the side too, and
 *    the label and timestamp belong to the GROUP, see below.)
 *  - Long bodies are cut off with an expand control. The panel docks beside the
 *    request and is narrow by design, so three pasted paragraphs currently make
 *    the thread unscrollable.
 *
 * ALIGNMENT IS STRUCTURAL, NOT A STYLE FLAG. A flex spacer sits on the opposite
 * side of the bubble rather than an `alignSelf` on it. Two reasons: it renders
 * identically on web, iOS and Android, and it is assertable positionally. A test
 * that reads back `alignSelf` only proves the test and the component agree about
 * a prop name — it would still pass if the component aligned every message the
 * same way.
 *
 * SINCE STORY 1069-1.3 THE ROW OFFERS EDIT AND DELETE (AC 35): on the viewer's
 * own live messages, "Delete" alone on anybody's live message when the viewer
 * is an IT Administrator (`mayDeleteAny`), nothing on a tombstone. The
 * affordance is a convenience; the server is the control (NFR8) and a refusal
 * is rendered by the panel, not swallowed. The body renders `messageBodyText`,
 * the v1 text projection of the stored HTML (AC 39), until Epic 4's rich viewer.
 *
 * SINCE STORY 1069-2.5 THE ACTIONS APPEAR ONLY WHEN YOU REACH FOR THEM. The
 * eligibility is computed here, exactly as before (`offerEdit` / `offerDelete`
 * below); what renders from it moved to `MessageActions.tsx`: on web one "…"
 * trigger at the bubble's trailing corner, hidden by opacity until the block
 * is hovered, the trigger focused or its menu open; on native a long press on
 * the bubble opening a chip at the same corner (the bottom sheet the story
 * shipped was replaced the same day, owner decision D-g), with the same verbs
 * exposed to screen readers as accessibility actions on the body. A message
 * that offers nothing mounts none of it. The FIRST eligible message on a
 * device carries a one-time hint that the gesture exists (`longPressHint`;
 * the list decides which row, and retires it). `Platform.OS` is read PER
 * RENDER, not at module load, so the tests can exercise both branches.
 *
 * SINCE STORY 1069-5.5 THE THREAD READS LIKE A CONVERSATION. Consecutive live
 * messages by one author within five minutes are a GROUP (the list decides,
 * `groupingAt`): the author label (`You` for the viewer) and the `Customer`
 * badge render once above the first bubble, the timestamp once below the
 * last, with ` · edited` when any message in the group was edited (Story 1.2
 * AC 29 as amended, D2). Other people's groups carry a 24px initials avatar
 * at the top-left of the group's FIRST bubble, overlapping its leading
 * edge by 6, with every bubble of the group and the author label above
 * starting 18 in (owner, 2026-09-23, quick-dev
 * spec-1069-thread-avatar-top-left, the Teams reference; position only). Their bubbles carry a 4px bottom-leading
 * corner; the viewer's own sit on `surfaceMessage` with a 4px bottom-trailing
 * corner; both keep the SOFT border (owner decision D1, 2026-09-11). The day
 * is said once, by a pill above the first message of each local day (D5).
 * A withdrawn message is one italic line between hairlines (D8). Every
 * message is still its own FlatList item; the group is a property of its
 * neighbours, computed at render and cached nowhere.
 *
 * SINCE STORY 1069-1.2 THE NAME AND THE INTERNAL/EXTERNAL FLAG COME OFF THE
 * WIRE. `message.authorName` is resolved server-side from `users`, and
 * `message.authorIsInternal` by the server's one classifier; the client has no
 * directory to look either up in (team rows are keyed by email, authors by
 * number). The "Customer" marker is `canSeeInternal && authorIsInternal === false`,
 * never a client team lookup.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Keyboard,
  Platform,
  Pressable,
} from 'react-native';

import { partitionForDisplay } from './attachmentPieces';
import {
  configTeamSuffix,
  spokenAuthor,
  withConfigTeamLabel,
} from './authorLabel';
import {
  AVATAR_COLUMN,
  BUBBLE_MAX_WIDTH,
  THREAD_AVATAR_SIZE,
} from './bubbleGeometry';
import {
  formatDayLabel,
  formatDayLabelSpoken,
  formatThreadWhen,
  formatWhen,
  initials,
} from './format';
import {
  LONG_PRESS_HINT_ANY,
  LONG_PRESS_HINT_OWN,
  MessageActions,
  NativeMessageActionsChip,
  anchorForChip,
  nativeAccessibilityActions,
  runNativeAccessibilityAction,
} from './MessageActions';
import { MessageAttachments } from './MessageAttachments';
import {
  MESSAGE_TRUNCATE_LINES,
  MessageBody,
  useBodyMeasure,
} from './MessageBody';
import { inlineFileIds } from './messageHtml';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';
import { ChevronDown, ChevronUp, MessageSquare } from '../icons';

import type { Message } from './correspondence.types';
import type { AnchorRect } from './MessageActions';
import type { GestureResponderEvent } from 'react-native';

/**
 * Re-exported for the row's consumers. Since #1530 the threshold lives with
 * the body (`MessageBody.tsx`), where it is a HEIGHT decided from `onLayout`
 * and no longer a `numberOfLines` estimated from the text: the old estimate
 * (48 characters a line, newlines counted) had nothing to say about a picture
 * or a table.
 */
export { MESSAGE_TRUNCATE_LINES };

/**
 * The bubble's fixed numbers live in `bubbleGeometry.ts` since #1710, where
 * the body derives an inline picture's width from the same values. Re-exported
 * here for the row's existing consumers.
 */
export { THREAD_AVATAR_SIZE };

/**
 * The verb phrase of the withdrawn line (Story 5.5, D8; C10): the rendered
 * string is `withdrawnLine(author, time)`, and the delete confirmation in the
 * panel quotes this phrase, so the two stay one source. Exported so tests and
 * the panel match the copy.
 */
export const MESSAGE_WITHDRAWN_TEXT = 'withdrew a message';

/**
 * The one line a withdrawn message renders as: `{author} withdrew a message
 * · {time}`. The same for every reader, the customer included: what was
 * withdrawn is not on the wire, and nothing here implies what it was.
 */
export const withdrawnLine = (author: string, timeText: string): string =>
  timeText
    ? `${author} ${MESSAGE_WITHDRAWN_TEXT} · ${timeText}`
    : `${author} ${MESSAGE_WITHDRAWN_TEXT}`;

/** The viewer's own group label (Story 5.5, AC 1). */
export const GROUP_AUTHOR_OWN = 'You';

/** The visible edited suffix on a group's timestamp (D2). */
export const GROUP_EDITED_SUFFIX = ' · edited';

/**
 * The bubble's corner (#1069 Story 8.4, D1, owner 2026-09-21). ONE constant
 * for the bubble, the jump mark that overlays it and `MessageList`'s pending
 * bubble, because a mark drawn at a different radius from its bubble sits
 * inside the corner. 6 is read off the owner's Teams reference
 * (`ux-1069-epic8-ref-teams-bubbles.png`: four equal corners at about 6 at
 * that scale, no tail, no border); Sally's 8 was convention and the owner's
 * number won.
 *
 * ⚠️ THE 4-POINT TAIL STAYS, AGAINST THE REFERENCE, AND THIS IS WHY. With the
 * border gone (D2) and the two fills 1.10:1 apart (D3), the lean and the
 * tail are the only non-hue "mine versus theirs" cues left, and a 6 corner
 * beside a 4 corner is still a visible tail. `DECIDED 2026-09-21 (owner):
 * "keep the tail."` Equal corners are a one-line change if that reverses.
 * The tail is `spacing.xs`, already a token; there is no 6 in `spacing.ts`
 * and a global token for one feature's bubble was not this story's call.
 */
export const BUBBLE_RADIUS = 6;

/**
 * What a row shows when neither the server nor the session can name the
 * author. Matches the server's own placeholder for a person it cannot name.
 */
export const UNKNOWN_AUTHOR_TEXT = 'Unknown user';

/**
 * The name a row shows. The wire name first; the viewer's own name only on the
 * viewer's own row (the one row whose name the client legitimately knows
 * without the server); then a placeholder. NEVER the numeric id (AC 28): it
 * is an identity, not a name, and the server has not sent an empty name since
 * review group 1, so the placeholder is defence rather than a path.
 *
 * Since Story 5.5 the viewer's own GROUP LABEL is `You` (`GROUP_AUTHOR_OWN`);
 * this name still feeds the expand control's spoken label and the withdrawn
 * line, so the fallback chain is unchanged.
 */
export const authorNameFor = (
  message: Pick<Message, 'authorName' | 'authorId'>,
  isOwn: boolean,
  viewerName: string | undefined,
): string =>
  message.authorName || (isOwn ? viewerName ?? '' : '') || UNKNOWN_AUTHOR_TEXT;

/**
 * How long a message stays MARKED after a jump lands on it (#1069 Story 7.6,
 * D5): the overlay fades from 1 to 0 over this, and `MessageList` clears the
 * mark at the same moment so the overlay UNMOUNTS whether or not the animation
 * finished. It lives here because the row owns the animation and
 * `MessageList` imports this file (the reverse would be a cycle); the list
 * re-exports it beside `MESSAGE_LIST_SETTLE_MS`, and the tests find the timer
 * by this delay (C10).
 */
export const MESSAGE_MARK_MS = 1500;

/**
 * The mark a jump leaves on the bubble it landed on (Story 7.6, D5): an
 * overlay INSIDE the bubble, `pointerEvents="none"`, filling it, a 2px border
 * in `colors.info` with the bubble's own radii (`BUBBLE_RADIUS`, and the
 * side's 4px corner mirrored; Story 8.4 D1 took both from 12 to 6). `'animated'` fades it out over `MESSAGE_MARK_MS` on the
 * native driver; `'static'` holds it at full strength for the same time (the
 * reduce-motion reader gets an INSTANT scroll, which is the hardest landing to
 * follow, so the one cue they need most is kept and only the fade goes, C3).
 *
 * ⚠️ AN OVERLAY, NOT THE BUBBLE'S OWN `borderColor` (D5). The bubble is a
 * `Pressable` on native and a `View` on web, and its hairline border is a
 * style with three review passes behind it; wrapping it in an animated
 * component or widening its border would touch that. This is additive and
 * unmounts.
 *
 * ⚠️ `colors.info`, NOT `surfaceMessage` (C2). `surfaceMessage` IS the own
 * bubble's fill, so a border in it is 1:1 on half the bubbles. `info` is the
 * thread's accent already and clears 3:1 on both grounds; the row suite pins
 * the two ratios beside the colour.
 */
const MessageMark: React.FC<{
  id: string;
  own: boolean;
  mark: 'animated' | 'static';
}> = ({ id, own, mark }) => {
  // Per mount: the list unmounts the overlay when it clears the mark, and
  // remounts it (`markKey`) when the same bubble is chosen again inside the
  // window, so every mark starts from 1.
  const opacity = useRef(new Animated.Value(1)).current;
  // Read in the body, never at module scope (7.2 D9). The native driver is
  // the native platforms' shape; react-native-web has no native animated
  // module and logs a warning for the flag before falling back to JS
  // (review 2026-09-19).
  const nativeDriver = Platform.OS !== 'web';
  useEffect(() => {
    if (mark !== 'animated') return undefined;
    const animation = Animated.timing(opacity, {
      toValue: 0,
      duration: MESSAGE_MARK_MS,
      useNativeDriver: nativeDriver,
    });
    animation.start();
    return () => {
      animation.stop();
    };
  }, [mark, opacity, nativeDriver]);
  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.mark,
        own ? styles.markOwn : styles.markOther,
        { opacity },
      ]}
      testID={`correspondence-message-mark-${id}`}
    />
  );
};

interface Props {
  message: Message;
  /**
   * Whether the VIEWER may see the internal space — not whether the author is
   * internal. Gates the "Customer" badge, which is third-person vocabulary: it
   * only makes sense to somebody on the inside of the distinction it describes.
   * Shown to an external viewer it discloses that we categorise participants,
   * which is the same inference UI-6 removed from the composer one component
   * away. Fails closed: absent means external, which sees less.
   */
  canSeeInternal?: boolean;
  /** `message.authorId === viewer.id`. The only alignment input. */
  isOwn: boolean;
  /** The viewer's own name, for the own-row fallback only. */
  viewerName?: string;
  /**
   * #1599 Story 1.5: the viewer's id, for the body's own-token tint. Absent
   * means nobody is "you": no tint. (The bubble's red `@` marker was removed
   * by owner ruling 2026-10-22: the tinted `@Name` already says it.)
   */
  viewerId?: string;
  /**
   * #1599 Story 1.7: the viewer holds Configuration Engineer, so the body
   * tints the team token as their own. Absent means not. Never on the
   * viewer's OWN message: the author gets no fan-out row (code review
   * 2026-10-22).
   */
  viewerIsConfigTeam?: boolean;
  /**
   * Grouping, decided by the list from this message's neighbours (Story 5.5,
   * `groupingAt`). A row rendered on its own is a group of one: every flag
   * defaults to the lone-message reading.
   */
  startsGroup?: boolean;
  /** The day pill renders inside this item, above the label (D5). */
  showDayDivider?: boolean;
  /**
   * The latest edit in the group, on the message that STARTS it (D2 as moved
   * 2026-10-05: the marker rides the header timestamp). The row ignores it
   * on a message that does not start its group.
   */
  groupEditedAt?: string;
  /** Offered on the viewer's own live messages (Story 1.3, AC 35). */
  onEdit?: (message: Message) => void;
  /** Offered on the viewer's own live messages, or on anybody's for an admin. */
  onDelete?: (message: Message) => void;
  /** `mayDeleteAnyMessage(viewer roles)`: IT Administrator. Fails closed. */
  mayDeleteAny?: boolean;
  /**
   * This message is the one in the composer (Story 2.5, AC 10). Its trigger
   * stays, with Edit disabled rather than hidden; Delete stays enabled, and
   * the panel already ends the edit when that message is deleted.
   */
  editing?: boolean;
  /**
   * Native only. The one-time "press and hold" hint renders under THIS row
   * (`MessageList` picks the first eligible row and owns the retirement):
   * `'own'` for the viewer's own message, `'any'` for an administrator's
   * delete-only case.
   */
  longPressHint?: 'own' | 'any';
  /** The hint's dismiss control. */
  onDismissLongPressHint?: () => void;
  /**
   * The reader reached the actions — by long press, or by a screen reader's
   * actions rotor / menu: the hint has done its job everywhere.
   */
  onLongPressUsed?: () => void;
  /**
   * Open one attachment (AC 12). The ROW does not open it: the viewers live
   * above this component and the panel owns them, so this reports WHICH file
   * was chosen and lets the panel decide which viewer takes it.
   *
   * `index` is into the message's images or its documents, matching `kind` —
   * which is what lets the image viewer page through the whole set from the
   * tile that was pressed.
   */
  onOpenAttachment?: (
    message: Message,
    index: number,
    kind: 'image' | 'document',
  ) => void;
  /** Save one attached file (Story 4.2, AC 12 as extended 2026-09-14). */
  onDownloadAttachment?: (message: Message, fileId: string) => void;
  /**
   * A jump landed here (Story 7.6, D5): draw the mark, fading or held. The
   * LIST owns which row carries it and for how long; the row only draws. No
   * mark on a tombstone, whatever this says: a withdrawn message has no
   * bubble to mark, and landing on the withdrawn line says enough (D13).
   */
  mark?: 'animated' | 'static';
  /**
   * Changes with every jump (the reveal's nonce). Keys the overlay so a
   * second jump to this same row inside the mark window remounts it and the
   * mark starts from full strength again (review 2026-09-19).
   */
  markKey?: number;
}

/**
 * The live bubble's container: a `Pressable` carrying the long press when
 * there is one, the plain `View` otherwise. Same style, same testID; only the
 * gesture differs, and only where there is something for it to open. The ref
 * is what the chip measures against.
 */
const Bubble: React.FC<{
  longPress?: (event: GestureResponderEvent) => void;
  bubbleRef: React.RefObject<View | null>;
  testID: string;
  own: boolean;
  children: React.ReactNode;
}> = ({ longPress, bubbleRef, testID, own, children }) => {
  const style = [styles.bubble, own ? styles.bubbleOwn : styles.bubbleOther];
  return longPress ? (
    <Pressable
      ref={bubbleRef}
      style={style}
      testID={testID}
      onLongPress={longPress}
      accessible={false}
    >
      {children}
    </Pressable>
  ) : (
    <View style={style} testID={testID}>
      {children}
    </View>
  );
};

/**
 * The thread avatar (Story 5.5, AC 7): 24px, the author's initials, on the
 * OTHER side only, at the top-left of the bubble of the
 * message that starts the group (owner, 2026-09-23, quick-dev
 * spec-1069-thread-avatar-top-left, the Teams reference; position only; it
 * used to sit at the bottom of the group's last bubble). No testID: it is derived, not
 * content.
 *
 * ⚠️ TAKES THE RAW `message.authorName`, NOT the resolved `authorName`
 * (code review, 2026-09-11). Fed the resolved one it read the
 * `UNKNOWN_AUTHOR_TEXT` fallback as a person and drew a circle saying "UU",
 * while the SAME author in the thread list showed a glyph — `initials` says
 * in its own docstring that a missing name "gives nothing, and the caller
 * shows a glyph instead", and Story 2.4 AC 28 is that initials are never
 * invented. A whitespace-only name tokenises to no words and lands here too,
 * rather than in an empty circle. The glyph matches the panel's
 * (`CorrespondencePanel.tsx`, Story 5.4 D5).
 *
 * ⚠️ HIDDEN FROM ASSISTIVE TECH ON BOTH PLATFORMS (D7): the name is already
 * spoken from the group label, and a `Text` of "MT" as the first thing in a
 * row would have VoiceOver open every message with two letters. The two RN
 * props are NATIVE ONLY — neither `accessibilityElementsHidden` nor
 * `importantForAccessibility` exists anywhere in react-native-web 0.21.2, so
 * on web they are dropped silently and every other-side group announced its
 * initials first (code review, 2026-09-11). `aria-hidden` is RNW's spelling
 * and is ignored by native, so all three are set together.
 *
 * ⚠️ A PLAIN GREY DISC: `surfaceAvatar` FILL, `textPrimary` INITIALS,
 * NO BORDER AND NO COLOUR (owner ruling, 2026-09-21, superseding Story 8.4
 * D5 the same day; fill darkened from `surfacePanelMuted` and initials
 * stepped from `textSecondary` on 2026-10-07, when the owner asked for a
 * disc that stands out more: 1.76:1 on white instead of 1.11:1, initials
 * 7.19:1). 8.4 made this a filled disc in one of eight hues with
 * white initials, hashed from the author's id; the owner saw it and reversed
 * it — the coloured discs are not wanted. **The reason is the ruling itself,
 * not a measurement**: the hues all cleared 4.5:1 against white initials and
 * were removed anyway, so nothing here is waiting to be "fixed" back.
 *
 * ⚠️ AND IT IS NOT THE PRE-8.4 DISC EITHER: borderless is a SEPARATE ruling.
 * Before 8.4 this disc carried a 1px `borderPanelSoft` ring; shown that a
 * muted disc on the white panel has an effectively invisible edge (1.11:1,
 * under the 3:1 shape floor), the owner chose borderless anyway. The weak
 * edge is accepted, recorded in the deferred-work ledger, and must not be
 * quietly re-ringed. The text obligation is carried by the initials:
 * `textSecondary` on `surfacePanelMuted` measures **5.16:1**, clearing 4.5:1.
 *
 * The name-less case is unchanged and is NOT part of the reversal: it stays
 * the dashed WHITE disc with a `textSecondary` glyph, the same treatment the
 * list gives it (8.4 C4 unified the two avatars' no-name styles, which was a
 * unification and not a colour decision). The author's id is no longer read
 * here at all, so the prop is gone.
 */
const ThreadAvatar: React.FC<{ name?: string }> = ({ name }) => {
  const letters = initials(name);
  return (
    <View
      style={
        letters === '' ? [styles.avatar, styles.avatarEmpty] : styles.avatar
      }
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      aria-hidden
    >
      {letters === '' ? (
        <MessageSquare size={12} color={colors.textSecondary} />
      ) : (
        <Text style={styles.avatarText}>{letters}</Text>
      )}
    </View>
  );
};

export const MessageRow: React.FC<Props> = ({
  message,
  canSeeInternal = false,
  isOwn,
  viewerName,
  viewerId,
  viewerIsConfigTeam = false,
  startsGroup = true,
  showDayDivider = false,
  groupEditedAt,
  onEdit,
  onDelete,
  mayDeleteAny = false,
  editing = false,
  longPressHint,
  onDismissLongPressHint,
  onLongPressUsed,
  onOpenAttachment,
  onDownloadAttachment,
  mark,
  markKey,
}) => {
  // Per message, not global. Expanding one long post must not expand the rest
  // of the thread underneath the reader.
  const [expanded, setExpanded] = useState(false);
  // Web only: the pointer is over the MESSAGE BLOCK, which reveals the "…"
  // (Story 2.5, AC 4). As props, not DOM listeners — react-native-web
  // forwards `onMouseEnter`/`onMouseLeave` and the test can reach a prop.
  const [hovered, setHovered] = useState(false);
  // Per render, never at module load (C7): the tests flip it.
  const web = Platform.OS === 'web';
  // Native only: the chip a long press opens, and where the bubble was when
  // it did (`null` = open but unmeasured; the chip then clamps to the edge).
  const [chipOpen, setChipOpen] = useState(false);
  const [chipRect, setChipRect] = useState<AnchorRect | null>(null);
  const bubbleRef = useRef<View>(null);

  const authorName = authorNameFor(message, isOwn, viewerName);
  /**
   * #1599 Story 4.1: the wire bit, already zeroed by the server while the
   * flag is off; `=== true` so an absent key (older server) labels nobody.
   * The avatar keeps the raw `message.authorName`: initials are the person's.
   */
  const configTeam = message.authorConfigTeam === true;
  const authorSpoken = spokenAuthor(authorName, configTeam);
  const withdrawn = message.deleted === true;
  /**
   * #1530: whether the body needs a Show more control is a MEASUREMENT of the
   * rendered HTML (`MessageBody`), never an estimate from the text. Until the
   * first layout the row offers no control and the body is clipped at the
   * collapsed height regardless, so nothing paints tall and then shrinks.
   */
  const { truncatable: bodyTruncatable, onContentHeight } = useBodyMeasure();
  // The bubble's file rows: every held file the body does not already show
  // inline. Index mapping back to the FULL lists lives beside it.
  const { rowFiles, fullPictureIndex, fullDocumentIndex } = useMemo(() => {
    const all = message.attachments ?? [];
    const inline = inlineFileIds(message.body);
    const rows = all.filter(f => !inline.has(f.id));
    const full = partitionForDisplay(all);
    const own = partitionForDisplay(rows);
    return {
      rowFiles: rows,
      fullPictureIndex: (i: number): number => {
        const id = own.pictures[i]?.id;
        return id === undefined
          ? -1
          : full.pictures.findIndex(f => f.id === id);
      },
      fullDocumentIndex: (i: number): number => {
        const id = own.documents[i]?.id;
        return id === undefined
          ? -1
          : full.documents.findIndex(f => f.id === id);
      },
    };
  }, [message.attachments, message.body]);
  const truncatable = !withdrawn && bodyTruncatable;
  // Stable per (message, handler): `MessageBody` is memoised on its props,
  // and a fresh closure here would defeat that on every row render.
  const openPicture = useMemo(
    () =>
      onOpenAttachment &&
      ((index: number) => onOpenAttachment(message, index, 'image')),
    [message, onOpenAttachment],
  );
  const offerEdit = !withdrawn && isOwn && !!onEdit;
  const offerDelete = !withdrawn && (isOwn || mayDeleteAny) && !!onDelete;
  /**
   * Is there anything to REACH? Edit that is offered but disabled (the
   * message already in the composer) is not, so it does not count here (code
   * review, 2026-09-10). Without the `!editing` term a message whose only
   * eligible verb was Edit still carried a long press and opened a chip with
   * one dead item in it, while `nativeAccessibilityActions` — which applies
   * the same `editing` rule — correctly declared nothing, so the touch path
   * and the screen-reader path disagreed about the same message.
   */
  /**
   * ⚠️ FILES MAKE THE MENU WORTH OPENING ON THEIR OWN (#1069 Story 4.2, owner
   * 2026-09-14). A message somebody ELSE sent offers neither Edit nor Delete,
   * so the "…" never appeared on it — and Download is the one action a reader
   * needs on another person's message. Without this, the only downloadable
   * attachments in the product would be your own.
   */
  /*
   * ⚠️ AND ONLY WHEN A DOWNLOAD IS WIRED (#1069 offline state, 2026-09-25),
   * the rule Edit and Delete already follow: no handler, no item. The panel
   * withholds the handler while the device is offline, and a Download item
   * that does nothing would be exactly the dead control this menu exists to
   * avoid.
   */
  const downloads = onDownloadAttachment
    ? (message.attachments ?? []).map(f => ({
        id: f.id,
        name: f.name,
      }))
    : [];
  const offersActions =
    offerDelete || (offerEdit && !editing) || downloads.length > 0;
  /*
   * ⚠️ A CHIP IS CLOSED, NOT MERELY HIDDEN, WHEN ITS ACTIONS GO (#1069 offline
   * state). The render gate below hides it while nothing is offered, but
   * `chipOpen` outlived the gate, so the chip reappeared by itself the moment
   * the actions came back (the device reconnecting) with nobody pressing.
   */
  useEffect(() => {
    if (!offersActions) setChipOpen(false);
  }, [offersActions]);

  const nativeActions = {
    offerEdit,
    offerDelete,
    editing,
    onEdit: () => onEdit?.(message),
    onDelete: () => onDelete?.(message),
  };
  // `null` when nothing is eligible: the row then DECLARES nothing (AC 8).
  const a11yActions =
    !web && offersActions ? nativeAccessibilityActions(nativeActions) : null;

  /**
   * Long press: measure the bubble, THEN open the chip at that rect, and
   * retire the hint. Measure-then-open, not open-then-measure: the measure is
   * asynchronous on the old architecture, and a chip mounted before it
   * resolves paints one frame at the window corner and jumps. A rect the
   * platform could not produce (no `measureInWindow`, or a detached view
   * reporting zeros) opens at the clamped fallback instead of not at all.
   * The keyboard goes first: a chip placed under an open keyboard cannot be
   * reached, and that is what iMessage does on a long press too.
   *
   * ⚠️ THE HINT IS RETIRED LAST, AFTER THE MEASURE (code review, 2026-09-10).
   * It used to be retired first, and retiring it unmounts a ~20pt row from
   * the list, which reflows every row below it. On the one long press that
   * matters — the first one ever made on the device, the only time the hint
   * is on screen — a press on any message below the hinted one measured the
   * pre-removal layout and then opened the chip into the post-removal one,
   * off by the hint's height. Measuring first closes the window entirely.
   */
  const openChip = (event?: GestureResponderEvent) => {
    if (typeof Keyboard?.dismiss === 'function') Keyboard.dismiss();
    // Where the finger actually was, for a bubble too tall to hang a chip on.
    const touchY = event?.nativeEvent?.pageY;
    const node = bubbleRef.current;
    if (node && typeof node.measureInWindow === 'function') {
      node.measureInWindow((x, y, width, height) => {
        setChipRect(
          width > 0 && height > 0
            ? anchorForChip({ top: y, left: x, width, height }, touchY)
            : null,
        );
        setChipOpen(true);
        onLongPressUsed?.();
      });
      return;
    }
    setChipRect(null);
    setChipOpen(true);
    onLongPressUsed?.();
  };

  const spacer = (
    <View
      style={styles.spacer}
      testID={`correspondence-message-spacer-${message.id}`}
    />
  );

  /**
   * The day label (Story 5.5, AC 11, D5; a plain label since Story 8.4 D4):
   * INSIDE the item, above the group label, so a prepend adds and removes no
   * rows and `maintainVisibleContentPosition` keeps anchoring on real
   * messages. It carries the item's top spacing when present; the row does
   * otherwise.
   */
  // ONE clock per render (code review, 2026-10-05): the divider and the
  // group timestamp band on the same `now`, so a render that straddles
  // midnight cannot say `Today` above `Yesterday 11:59 PM`.
  const now = new Date();
  const divider = showDayDivider ? (
    <View
      style={styles.dayDividerWrap}
      testID={`correspondence-day-divider-${message.id}`}
    >
      <Text
        style={styles.dayLabel}
        accessibilityLabel={formatDayLabelSpoken(message.sentAt)}
      >
        {formatDayLabel(message.sentAt, now)}
      </Text>
    </View>
  ) : null;

  // The space above this item: 8 under a day label, 16 between groups, 4
  // inside one. Decided here, not by a list-level gap (see `bodyContent`).
  const rowSpacing = showDayDivider
    ? styles.rowAfterDivider
    : startsGroup
    ? styles.rowStartsGroup
    : styles.rowInGroup;

  /**
   * THE GROUP'S TIMESTAMP (AC 1): on the author line of the message that
   * STARTS the group, after the name and badge, in Teams' age band
   * (`formatThreadWhen`), with ` · edited` when any message in the group was
   * edited (D2) and the absolute form (plus the absolute edited time) in the
   * spoken label, so a screen reader still hears the date (Story 1.2 AC 29).
   * The testID is `-edited-` on an edited group, `-when-` otherwise, keyed
   * by this FIRST message's id.
   *
   * History: under the last bubble, clock alone, from Story 5.5 (D6) until
   * 2026-10-05, when the owner asked for the name and time together, as
   * Teams' header (`Wilson, Phil  Thursday 2:27 PM`). Two consequences of
   * "the first message's time" that are Teams' too and are accepted, not
   * defects: withdrawing a group's FIRST message re-heads the group on the
   * next live message and the header time moves to it (the tombstone keeps
   * its own time); and a screen reader now hears the time BEFORE the body
   * rather than after the group. Nothing renders when there is no time to
   * show and no edit (an unparseable `sentAt`), so no empty node eats the
   * row's gap.
   */
  const groupTimeText =
    formatThreadWhen(message.sentAt, now) +
    (groupEditedAt ? GROUP_EDITED_SUFFIX : '');
  const groupTime =
    startsGroup && groupTimeText ? (
      <Text
        style={styles.groupTime}
        testID={
          groupEditedAt
            ? `correspondence-message-edited-${message.id}`
            : `correspondence-message-when-${message.id}`
        }
        accessibilityLabel={
          groupEditedAt
            ? `${formatWhen(message.sentAt)}, edited ${formatWhen(
                groupEditedAt,
              )}`
            : formatWhen(message.sentAt)
        }
      >
        {groupTimeText}
      </Text>
    ) : null;

  const row = withdrawn ? (
    /**
     * A TOMBSTONE (Story 1.2 AC 29 as amended by Story 5.5, D8): one italic
     * line between hairlines, `{author} withdrew a message · {time}`, on
     * neither side. No attribution block, no bubble, no avatar, no expand
     * control, no edited marker, no attachment. The same for every reader,
     * the customer included: what was withdrawn is not on the wire, and
     * nothing here implies what it was. It never joins a group, so its
     * spacing is a group's. The row and block testIDs survive (AC 17).
     */
    <View
      style={[styles.row, rowSpacing]}
      testID={`correspondence-message-row-${message.id}`}
    >
      <View
        style={styles.tombstoneColumn}
        testID={`correspondence-message-${message.id}`}
      >
        <View
          style={styles.tombstone}
          testID={`correspondence-message-tombstone-${message.id}`}
        >
          <View style={styles.tombstoneRule} />
          {/**
           * ⚠️ TWO LINES, NOT ONE (code review, 2026-09-11). The hairlines
           * are `flex: 1` with a zero basis, so they shrink to nothing and
           * this text absorbs every pixel of overflow on its own. At
           * `numberOfLines={1}` a long display name therefore ate the rest
           * of its own sentence: `Christopher Bartholomew-Fitzge…`, with
           * "withdrew a message" and the time both behind the ellipsis, left
           * an unexplained grey name between two rules. The name comes
           * first and cannot be reordered (D8 fixes the string), and
           * `ellipsizeMode="head"` is native-only — react-native-web maps
           * only `clip` and `tail` — so wrapping is the one fix that holds
           * on all three platforms. D8's "a single line" is still what every
           * real name renders as; two is the bound on the pathological one.
           */}
          <Text style={styles.tombstoneText} numberOfLines={2}>
            {withdrawnLine(
              withConfigTeamLabel(authorName, configTeam),
              formatThreadWhen(message.sentAt, now),
            )}
          </Text>
          <View style={styles.tombstoneRule} />
        </View>
      </View>
    </View>
  ) : (
    <View
      style={[styles.row, rowSpacing]}
      testID={`correspondence-message-row-${message.id}`}
    >
      {isOwn ? spacer : null}

      <View
        style={[styles.column, isOwn && styles.columnOwn]}
        testID={`correspondence-message-${message.id}`}
        {...(web && offersActions
          ? ({
              onMouseEnter: () => setHovered(true),
              onMouseLeave: () => setHovered(false),
            } as Record<string, unknown>)
          : {})}
      >
        {/**
         * THE GROUP LABEL, once per group, on the message that STARTS it
         * (AC 1): the author (`You` for the viewer), the `Customer` badge
         * (AC 2) and, since 2026-10-05, the group's timestamp (Teams' header
         * order), outside the bubble, above the first bubble. Right-aligned
         * on the viewer's own side. On the other side it starts 18 in,
         * level with the BUBBLE's leading edge, not the disc's (owner,
         * 2026-09-23, quick-dev spec-1069-thread-avatar-top-left, the Teams
         * reference measured at 8x).
         */}
        {startsGroup ? (
          <View
            style={[
              styles.groupHead,
              isOwn ? styles.groupHeadOwn : styles.groupHeadOther,
            ]}
          >
            {/* #1599 Story 4.1 (frame H1): the name and the label are two
                Texts in one row so the NAME truncates and the label never
                does. Geometry is walk-verified; tests pin the declaration. */}
            <View style={styles.groupAuthorLine}>
              <Text style={styles.groupAuthor} numberOfLines={1}>
                {isOwn ? GROUP_AUTHOR_OWN : authorName}
              </Text>
              {configTeam ? (
                <Text
                  style={styles.groupAuthorLabel}
                  numberOfLines={1}
                  testID={`correspondence-message-config-team-${message.id}`}
                >
                  {configTeamSuffix}
                </Text>
              ) : null}
            </View>
            {canSeeInternal && message.authorIsInternal === false ? (
              <Text style={styles.msgExtBadge}>Customer</Text>
            ) : null}
            {groupTime}
          </View>
        ) : null}

        {/**
         * THE BUBBLE ROW: on the other side it is inset by the 18 avatar
         * column on EVERY message, so every bubble of the group starts at the
         * same x, and the message that STARTS the group draws the disc at the
         * row's top-left, overlapping the bubble's leading edge by 6 (owner,
         * 2026-09-23, quick-dev spec-1069-thread-avatar-top-left, the Teams
         * reference; it used to sit at the bottom of the group's last bubble,
         * beside it). The viewer's own side has no avatar and no inset.
         *
         * The disc is absolutely positioned INSIDE this row's bounds (left 0,
         * top 0), never hung outside the block, and it is the row's LAST child
         * so it paints over the bubble on every platform by sibling order,
         * not by `zIndex`. `pointerEvents="none"`: the 6 it covers still
         * belong to the bubble's long press and hover. Painted after the
         * bubble, it also covers the top-left corner of the jump mark's ring
         * on a group's first message; accepted, the rest of the ring reads.
         *
         * The row is INSIDE the message block, not a sibling of it in the
         * row: the row's two children (block, spacer) are the positional
         * alignment contract every alignment test asserts, and the block's
         * 85% cap then bounds the avatar and the bubble as one unit.
         */}
        <View
          style={[styles.bubbleRow, isOwn ? null : styles.bubbleRowOther]}
          testID={`correspondence-message-bubble-row-${message.id}`}
        >
          {/**
           * THE BUBBLE. On native an eligible message's bubble is a
           * `Pressable` so a long press opens the actions chip (Story 2.5
           * AC 7, as amended by D-g) — `accessible={false}`, DELIBERATELY: an
           * accessible parent becomes one element to VoiceOver and its
           * children (the expand control) stop being reachable. The verbs a
           * screen reader gets live on the BODY wrapper below instead. On
           * web, and for a message that offers nothing, it is the plain
           * `View` it always was.
           */}
          <Bubble
            longPress={!web && offersActions ? openChip : undefined}
            bubbleRef={bubbleRef}
            own={isOwn}
            testID={`correspondence-message-bubble-${message.id}`}
          >
            {a11yActions ? (
              /**
               * The accessibility-action carrier (AC 8): the body text and
               * nothing else, so what a screen reader focuses reads as the
               * message (no label of its own, AC 13) and its actions rotor /
               * menu offers the same verbs as the long press. Only the body:
               * on the old architecture (`newArchEnabled=false`) iOS's
               * `RCTTextView` is a `UIView`, not an `RCTView`, so actions put
               * on the `Text` itself are dropped; and wrapping any more than
               * the body would swallow the expand control (see the bubble).
               */
              <View
                accessible
                accessibilityActions={a11yActions}
                onAccessibilityAction={event => {
                  // The rotor is the other way to reach the actions; a hint
                  // that says "press and hold" has nothing left to teach.
                  onLongPressUsed?.();
                  runNativeAccessibilityAction(nativeActions, event);
                }}
                testID={`correspondence-message-body-${message.id}`}
              >
                <MessageBody
                  message={message}
                  expanded={expanded}
                  onContentHeight={onContentHeight}
                  onOpenPicture={openPicture}
                  viewerId={viewerId}
                  viewerIsConfigTeam={viewerIsConfigTeam && !isOwn}
                />
              </View>
            ) : (
              <MessageBody
                message={message}
                expanded={expanded}
                onContentHeight={onContentHeight}
                onOpenPicture={openPicture}
                viewerId={viewerId}
                viewerIsConfigTeam={viewerIsConfigTeam && !isOwn}
              />
            )}

            {truncatable ? (
              <TouchableOpacity
                onPress={() => setExpanded(v => !v)}
                style={styles.expandBtn}
                testID={`correspondence-message-expand-${message.id}`}
                accessibilityRole="button"
                /**
                 * `expanded` state as well as the label, and the author in the name.
                 * A screen-reader user swiping a ten-message thread would otherwise
                 * hear ten buttons called "Expand this message".
                 */
                accessibilityState={{ expanded }}
                accessibilityLabel={
                  expanded
                    ? `Collapse message from ${authorSpoken}`
                    : `Expand message from ${authorSpoken}`
                }
              >
                <Text style={styles.expandText}>
                  {expanded ? 'Show less' : 'Show more'}
                </Text>
                {/* The chevron takes the label's colour, not the brand's. It is
                    half of ONE control, and a blue word beside an orange arrow
                    reads as two things that happen to touch (#1069 UI-8).
                    `textLink` with the label since Story 8.4 D6: `info` was
                    3.29:1 on the own bubble, under SC 1.4.3. */}
                {expanded ? (
                  <ChevronUp size={12} color={colors.textLink} />
                ) : (
                  <ChevronDown size={12} color={colors.textLink} />
                )}
              </TouchableOpacity>
            ) : null}

            {/* Story 4.2 Tasks 3-5. ⚠️ `onLongPress` IS FORWARDED, AND C3 IS
                WHY: a file row is a `Pressable` CHILD, so its press does not
                bubble to the bubble's own long press. Without this a files-only
                message would be the one message on native with no route to the
                actions chip.

                ⚠️ AND THERE IS NO TONE PROP HERE ANY MORE (Story 8.5, C4/D5).
                8.4's D7 passed `onMutedGround={!isOwn}` so a file's fill sat
                one tone away from its bubble. A Story 8.5 row has NO FILL --
                the bubble is the ground -- so there is nothing to flip, and
                the one tone that remains (the thumbnail's box) is white on
                both sides and measured. That is not a reversal of D7: its
                purpose was "never invisible against your own bubble", and a
                row with no fill cannot be. */}
            {rowFiles.length ? (
              <MessageAttachments
                files={rowFiles}
                // Absent when the panel wires no opener (offline), so the
                // rows go inert rather than taking a tap that does nothing.
                // ⚠️ THE ROWS INDEX THEIR OWN LIST; THE VIEWER INDEXES THE
                // MESSAGE'S. A picture shown inline is not a row (owner,
                // 2026-10-01), so a row's index is mapped back through the
                // file id to the index the panel resolves over the full
                // `attachments`.
                onOpen={
                  onOpenAttachment &&
                  (index => {
                    const full = fullPictureIndex(index);
                    if (full >= 0) onOpenAttachment(message, full, 'image');
                  })
                }
                onOpenDocument={
                  onOpenAttachment &&
                  (index => {
                    const full = fullDocumentIndex(index);
                    if (full >= 0) onOpenAttachment(message, full, 'document');
                  })
                }
                onLongPress={
                  !web && offersActions
                    ? () => {
                        openChip();
                        onLongPressUsed?.();
                      }
                    : undefined
                }
              />
            ) : null}

            {/* The "…" (Story 2.5, AC 3): web only, eligible messages only,
                LAST so it paints above the body and takes the pointer. It
                positions itself at the bubble's top-trailing corner. */}
            {web && offersActions ? (
              <MessageActions
                downloads={downloads}
                onDownload={fileId => onDownloadAttachment?.(message, fileId)}
                messageId={message.id}
                isOwn={isOwn}
                authorName={authorSpoken}
                offerEdit={offerEdit}
                offerDelete={offerDelete}
                hovered={hovered}
                editing={editing}
                onEdit={nativeActions.onEdit}
                onDelete={nativeActions.onDelete}
              />
            ) : null}

            {/* The jump's mark (Story 7.6, D5): last of all, so it paints
                above everything in the bubble; `pointerEvents="none"` so the
                "…" above and the tiles keep the pointer. Only the LIVE bubble
                has one: the tombstone branch above draws none, whatever the
                list says (D13). */}
            {mark ? (
              <MessageMark
                key={markKey}
                id={message.id}
                own={isOwn}
                mark={mark}
              />
            ) : null}
          </Bubble>
          {!isOwn && startsGroup ? (
            <View style={styles.avatarPin} pointerEvents="none">
              <ThreadAvatar name={message.authorName} />
            </View>
          ) : null}
        </View>

        {/* Native: the chip a long press opened (D-g). A `Modal`, so it is
            not in the row's layout and nothing reflows. */}
        {!web && chipOpen && offersActions ? (
          <NativeMessageActionsChip
            downloads={downloads}
            onDownload={fileId => onDownloadAttachment?.(message, fileId)}
            messageId={message.id}
            anchorRect={chipRect}
            offerEdit={offerEdit}
            offerDelete={offerDelete}
            editing={editing}
            onEdit={nativeActions.onEdit}
            onDelete={nativeActions.onDelete}
            onClose={() => setChipOpen(false)}
          />
        ) : null}
      </View>

      {isOwn ? null : spacer}
    </View>
  );

  /**
   * Native: the one-time hint (D-i), only on the row the list chose. A
   * SIBLING BELOW THE ROW at the list's full width, never inside the message
   * column: the column sizes to its widest child, so a 300pt line of hint
   * inside it stretched a one-word bubble to the 85% cap and snapped it back
   * when the hint retired (found at code review, reproduced on Yoga: 120pt →
   * 280pt). Out here it costs the row nothing and wraps at the list's width.
   * On the other side it is indented by the avatar column (Story 5.5, C5) so
   * it still lines up with the bubble it points at.
   */
  const hint =
    !web && longPressHint && offersActions ? (
      <View
        style={[
          styles.hintRow,
          isOwn ? styles.hintRowOwn : styles.hintRowOther,
        ]}
        testID={`correspondence-long-press-hint-${message.id}`}
      >
        <Text style={styles.hintText}>
          {longPressHint === 'own' ? LONG_PRESS_HINT_OWN : LONG_PRESS_HINT_ANY}
        </Text>
        <Pressable
          onPress={onDismissLongPressHint}
          style={styles.hintDismiss}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          testID={`correspondence-long-press-hint-dismiss-${message.id}`}
          accessibilityRole="button"
          accessibilityLabel="Dismiss hint"
        >
          <Text style={styles.hintDismissText}>×</Text>
        </Pressable>
      </View>
    ) : null;

  return divider || hint ? (
    <>
      {divider}
      {row}
      {hint}
    </>
  ) : (
    row
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-start' },
  /** The item's own top spacing (Story 5.5, AC 5): the list has no gap. */
  rowStartsGroup: { marginTop: spacing.lg },
  rowInGroup: { marginTop: spacing.xs },
  rowAfterDivider: { marginTop: spacing.sm },
  /**
   * The spacer is what carries the alignment, and `minWidth` is its floor. On
   * its own a `flex: 1` spacer collapses to nothing as soon as the body fills
   * the column, so "mine" and "theirs" would become indistinguishable on
   * exactly the long messages the review complained about. `BUBBLE_MAX_WIDTH`
   * on the bubble is the other half of the same guarantee: the lean stays
   * proportional instead of degrading to a 20px indent.
   */
  spacer: { flex: 1, minWidth: spacing.xl },
  /**
   * The message BLOCK: group label
   * above (with the group's timestamp on it since 2026-10-05), bubble row,
   * nothing below. The width cap lives here
   * rather than on the bubble, so the label, the avatar and the bubble lean
   * as one unit — capping only the
   * bubble would let a long author line push the block wider than the
   * message it labels.
   */
  column: {
    flexShrink: 1,
    maxWidth: BUBBLE_MAX_WIDTH,
    gap: spacing.xs,
  },
  // The viewer's own block right-aligns its label, bubble and timestamp.
  columnOwn: { alignItems: 'flex-end' },

  /** The bubble's row; a row so the bubble sizes to its content. */
  bubbleRow: {
    flexDirection: 'row',
    maxWidth: '100%',
  },
  /** The 18 avatar column, on every other-side message (2026-09-23). */
  bubbleRowOther: { paddingLeft: AVATAR_COLUMN },
  /** The disc at the row's top-left, over the bubble's leading 6. */
  avatarPin: { position: 'absolute', left: 0, top: 0 },
  /**
   * The plain grey disc (owner ruling 2026-09-21, superseding Story 8.4 D5
   * the same day): `surfacePanelMuted` fill, no border, no per-author hue.
   * 8.4 chose the fill at the CALL SITE from an eight-hue palette; the palette
   * and its hash are deleted, so the fill lives here again. The border is not
   * coming back either — see `ThreadAvatar`'s docblock for why borderless is
   * its own ruling and why the 1.11:1 edge is accepted rather than fixed.
   * Mirrors the panel's `threadAvatar` / `threadAvatarEmpty` pair at 24
   * instead of 28.
   */
  avatar: {
    width: THREAD_AVATAR_SIZE,
    height: THREAD_AVATAR_SIZE,
    borderRadius: THREAD_AVATAR_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAvatar,
  },
  /** The name-less author: a dashed white disc around the glyph (C4). */
  avatarEmpty: {
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderPanel,
    borderStyle: 'dashed',
  },
  /**
   * `textSecondary` on the muted fill, **5.16:1** (owner ruling 2026-09-21,
   * superseding Story 8.4 D5). 8.4 had these initials `textLight`, which was
   * white-on-hue and legible only BECAUSE of the hue; with the hue gone white
   * would be 1.11:1 on the muted disc. The initials carry the whole text
   * obligation here, since the disc itself is borderless by the same ruling.
   */
  avatarText: { fontSize: 10, fontWeight: '700', color: colors.textPrimary },

  /**
   * The bubble. Since Story 5.5 (owner decision D1, 2026-09-11) the FILL and
   * the corner RADII follow the side, alongside the alignment that always
   * did: the viewer's own on `surfaceMessage` with a 4px bottom-trailing
   * corner, everyone else's on `surfaceMessageOther` (white until Story 8.4
   * D3, `surfacePanelMuted` until 2026-10-07 when both fills took Teams'
   * values) with a 4px bottom-leading corner. Ownership is still never colour
   * alone: a colour-blind reader sees the lean, the tail corner, the `You`
   * label and the avatar.
   *
   * ⚠️ NO BORDER, AND THE PROPERTIES ARE REMOVED, NOT ZEROED (Story 8.4 D2,
   * owner 2026-09-21, against the Teams reference
   * `ux-1069-epic8-ref-teams-bubbles.png`). The history, so nobody restores
   * it from the old comment: the border was softened to `borderPanelSoft` on
   * 2026-09-09 ("the border color on the messages seems a little
   * distracting"), removal was offered and DECLINED on 2026-09-11, and the
   * docblock here said "do not finish the job later". The owner reversed
   * that on 2026-09-21 with the reference in hand: a live bubble has no
   * border; the pending bubble's dashed edge (`MessageList`) is now the ONLY
   * border in the thread. A `borderColor` left beside `borderWidth: 0`
   * would be a border waiting to be turned back on, so both are gone and the
   * suite pins them `undefined`. The TOMBSTONE still has no bubble at all
   * (D8, {@link tombstone}). `borderRadius: BUBBLE_RADIUS` is the base so the
   * corner overrides read as exceptions to it (D1: 6 with the 4 tail kept).
   */
  bubble: {
    gap: spacing.xs,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    borderRadius: BUBBLE_RADIUS,
    flexShrink: 1,
  },
  /**
   * ⚠️ D4 AND AC 11 ARE WRONG ON WEB, AND THIS IS THE RECORD OF IT (found in
   * the running app, 2026-09-14). They specify `alignSelf: 'stretch'` on a
   * bubble carrying attachments, to give the percentage-width grid "a definite
   * parent". `bubbleRow` is `flexDirection: 'row'`, so `alignSelf` governs the
   * CROSS axis — VERTICAL here. It stretched the bubble's HEIGHT and left its
   * width content-sized, so the tile took the width of the text beside it
   * (about 30px for a message reading "test", and zero with no text).
   *
   * ⚠️ AND THE OBVIOUS REPAIR IS WORSE. Making the bubble `flexGrow: 1` and
   * stopping the spacer growing DOES widen it — and moves every own-side
   * message to the LEFT, because THE SPACER IS THE LEAN: it is what pushes an
   * own bubble to its side. Shipped for about a minute and caught in the app.
   *
   * The bubble is therefore left exactly as it was, and the grid stopped
   * needing a definite parent instead — see `MessageAttachments`, which sizes
   * its tiles in points.
   */
  bubbleOwn: {
    backgroundColor: colors.surfaceMessage,
    borderBottomRightRadius: spacing.xs,
  },
  bubbleOther: {
    backgroundColor: colors.surfaceMessageOther,
    borderBottomLeftRadius: spacing.xs,
  },
  /**
   * The jump's mark (Story 7.6, D5): fills the bubble (the four edges written
   * out rather than `StyleSheet.absoluteFillObject`, which the test harness's
   * `StyleSheet` mock does not carry), takes no pointer, and mirrors the
   * bubble's radii so the border sits on its edge and not inside it. The 2px
   * stays after Story 8.4 took the bubble's own hairline away: `info` is
   * 3.29:1 on `surfaceMessage` and 3.61:1 on `surfacePanelMuted`, and at 1px
   * that is a thin line on a tinted ground, not a mark. The radius is the
   * bubble's `BUBBLE_RADIUS` so the two cannot drift apart.
   */
  mark: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderWidth: 2,
    borderColor: colors.info,
    borderRadius: BUBBLE_RADIUS,
  },
  markOwn: { borderBottomRightRadius: spacing.xs },
  markOther: { borderBottomLeftRadius: spacing.xs },

  /**
   * The withdrawn line (D8): a rule flexing on each side of one italic line.
   * ⚠️ NO FILL, NO BORDER, NO RADIUS: it is deliberately NOT a bubble, so
   * "gone" is a difference of structure, not of tone (this supersedes Story
   * 5.1's D1 "muted ground" for the tombstone; pending rows keep it).
   */
  tombstoneColumn: { flex: 1, minWidth: 0 },
  tombstone: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  tombstoneRule: {
    flex: 1,
    height: 1,
    backgroundColor: colors.borderPanelSoft,
  },
  tombstoneText: {
    flexShrink: 1,
    fontSize: 11,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },

  /** The group label row: author (or `You`) and the Customer badge. */
  groupHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    // Outside the bubble there is no container padding to sit in, so the text
    // needs its own small inset. The other side trades the LEADING half for
    // an 18 margin, so the name starts level with the bubble (2026-09-23).
    paddingHorizontal: spacing.xs,
  },
  groupHeadOwn: { justifyContent: 'flex-end' },
  // Level with the bubble's leading edge, as in the Teams reference (2026-09-23).
  groupHeadOther: { paddingLeft: 0, marginLeft: AVATAR_COLUMN },
  // #1599 Story 4.1: shrinkable so a long name ellipsizes inside the row
  // instead of the row wrapping the label onto its own line.
  groupAuthorLine: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
  },
  groupAuthor: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textSecondary,
    flexShrink: 1,
  },
  // Never shrinks: the label is shown whole and the name gives way (H1).
  groupAuthorLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textSecondary,
    flexShrink: 0,
  },
  msgExtBadge: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: colors.textOnWarningMuted,
    backgroundColor: colors.surfaceWarningMuted,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
    overflow: 'hidden',
  },
  /**
   * The group timestamp, ON THE AUTHOR LINE after the name and badge
   * (2026-10-05; it was under the last bubble, `groupWhen`, 10pt, with its
   * own inset). The row's `gap` spaces it; the name's size, regular weight.
   */
  groupTime: { fontSize: 11, color: colors.textSecondary },

  /**
   * The day label (AC 11): centred, mixed case (since 2026-10-05, see
   * `dayLabel`), PLAIN TEXT on the panel ground
   * since Story 8.4 D4 (owner 2026-09-21; the pill's fill, radius, horizontal
   * padding and `overflow` are gone). `textSecondary` on `backgroundPrimary`
   * is 5.74:1. `paddingVertical: 3` is kept so the divider's height and the
   * "8 under a day label" rhythm do not move.
   */
  dayDividerWrap: {
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  // Mixed case and no tracking since 2026-10-05: the label now reads
  // `Today` / `Yesterday` / `Thursday` / `September 1`, the way Teams
  // prints it, so `textTransform: 'uppercase'` and `letterSpacing: 0.6`
  // went. Size, weight, colour and padding stay so the divider's height and
  // the "8 under a day label" rhythm do not move.
  dayLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.textSecondary,
    paddingVertical: 3,
  },

  // The singular `attachment` / `attachmentName` / `attachmentSize` styles that
  // sat here were orphaned when `message.attachment` became
  // `message.attachments` and the chip moved into `MessageAttachments`
  // (Story 4.2 Task 4). Removed at code review, 2026-09-14, with the two dead
  // imports they were the last users of.

  expandBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
  },
  /** The one-time long-press hint: the timestamp's register, one line, a ×. */
  hintRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.xs,
    // Closes most of the item's spacing so the line reads as the message's.
    marginTop: -spacing.xs,
  },
  hintRowOwn: { justifyContent: 'flex-end' },
  // Under its bubble, past the avatar column (Story 5.5, C5).
  hintRowOther: { marginLeft: AVATAR_COLUMN },
  hintText: {
    flexShrink: 1,
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.formLabel,
    color: colors.textSecondary,
  },
  hintDismiss: { paddingHorizontal: spacing.xs },
  hintDismissText: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textSecondary,
  },
  expandText: {
    fontSize: fontSize.charCount,
    // `lineHeight.charCount` is 12 — equal to the font size, and scoped by its
    // own docstring to a single-line "0 / 10000" counter. `formLabel` (16) is
    // the nearest token with real leading.
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.semibold,
    /**
     * BLUE, not brand orange (#1069 UI-8): "Show more" is a link inside a
     * message, and orange here competed with the confidentiality cues the
     * panel spends it on. `textLink`, not `info`, since Story 8.4 D6: `info`
     * measured 3.29:1 on `surfaceMessage` and 3.61:1 on `surfacePanelMuted`,
     * under SC 1.4.3's 4.5:1, and was the ledger's open pair since 5.1.
     * `textLink` is 6.73:1 and 7.39:1 on the same fills, and is already the
     * panel's "text action" token (the team strip's Show/Hide). `info` stays
     * for SHAPES only (the jump mark, the progress bar, the spinner).
     */
    color: colors.textLink,
  },
});
